import { ModalService } from './../../../base/shared/modal/modal.service';
import { Component, ViewChild, ViewContainerRef, OnDestroy, Input } from '@angular/core';
import { ChangeEventArgs, DashboardLayoutComponent, DashboardLayoutModule, ResizeArgs } from '@syncfusion/ej2-angular-layouts';
import { DashboardPanelDTO, UpdateDashboardPanelsDTO } from '../../../base/models/models.swagger.generated';
import { DashboardService, IExtendedDashboardDTO, ExtendedPanelModel } from './dashboard.service';
import { WidgetType } from './widgets/shared/widget-type.enum';
import { IAbstractWidgetComponent, ParameterChangedData, WidgetMode } from './widgets/shared/abstract-widget.component';
import { Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { AddWidgetDialogComponent, WidgetSelectedData } from "./add-widget-dialog/add-widget-dialog.component";

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    imports: [DashboardLayoutModule, TranslateModule, AddWidgetDialogComponent]
})
export class DashboardComponent implements OnDestroy {

    @Input()
  public set isEditMode(value: boolean) {
    this._isEditMode = value;
    this.refreshWidgetData();
  }

  public get isEditMode(): boolean {
    return this._isEditMode
  }

  public dashboardId: number;

  @ViewChild(DashboardLayoutComponent)
  public dashboard: DashboardLayoutComponent;

  @ViewChild('viewContainer', {read: ViewContainerRef, static: true})
  public viewContainer: ViewContainerRef;

  @ViewChild('addWidgetDialog')
  public addWidgetDialog: AddWidgetDialogComponent;

  public cellSpacing: number[] = [5, 5];
  public cellColumns: number = 12;

  private extendedDashboardPanels: ExtendedPanelModel[] = [];

  private panelAdded = false;
  private panelDeleted = false;
  private subscriptions = new Subscription();
  private _isEditMode = false;

  constructor(
    private dashboardService: DashboardService,
    private modalService: ModalService) {
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public onInitDashboard() {
    //Workaround to render correct width of panels and widgets
    setTimeout(() => {
      this.subscriptions.add(
        this.dashboardService.dashboards$.subscribe((dashboards: IExtendedDashboardDTO[]) => {
          this.extendedDashboardPanels = dashboards.find(d => d.id === this.dashboardId)?.extendedDashboardPanels || [];
          this.reloadWidgets();
        })
      );
      this.dashboard.refresh();
    }, 500);
  }

  public onResizeStop(args: ResizeArgs) {
    //Get panel from params
    const panel = this.dashboard.panels.find(p => p.id === args.element.id) as ExtendedPanelModel;
    if (!panel) {
      return;
    }

    this.dashboardService.updateDashboardPanels(new UpdateDashboardPanelsDTO({
      dashboardId: this.dashboardId,
      changedPanels: [this.mapToDashboarPanelDTO(panel)],
    })).subscribe();
  }

  public onChange(args: ChangeEventArgs) {
    if (!args.isInteracted && !this.panelAdded && !this.panelDeleted) {
      return;
    }

    this.panelAdded = false;
    this.panelDeleted = false;

    this.dashboardService.updateDashboardPanels(new UpdateDashboardPanelsDTO({
      dashboardId: this.dashboardId,
      addedPanels: args.addedPanels?.map(p => this.mapToDashboarPanelDTO(p as ExtendedPanelModel)),
      changedPanels: args.changedPanels?.map(p => this.mapToDashboarPanelDTO(p as ExtendedPanelModel)),
      removedPanelIds: []
    })).subscribe();
  }

  public onSaveAddWidgetDialog(widgetSelectedData: WidgetSelectedData): void {
    const widgetClass = this.dashboardService.getWidgetClass(widgetSelectedData.widgetType);
    const component = this.viewContainer.createComponent(widgetClass);
    const instance = component.instance as IAbstractWidgetComponent;
    instance.isPersonalMode = this.dashboardService.isPersonalMode(widgetSelectedData.widgetType);
    this.viewContainer.insert(component.hostView);
    this.panelAdded = true;
    const newPanel = {
      id: '0',
      sizeX: component?.instance?.defaultSizeX,
      sizeY: component?.instance?.defaultSizeY,
      row: 0,
      col:0,
      widgetType: widgetSelectedData.widgetType,
      widgetParameters: widgetSelectedData.parameters?.serializedParamerters
    } as ExtendedPanelModel;

    //If pannel colides, add to dashboard and let the dashboardcomponent handle the reshuffeling and updating
    //Otherwise update the panels directly
    if (this.dashboardService.doesPanelCollide(this.dashboardId, newPanel)) {
      this.dashboard.addPanel(newPanel);
      const panel = document.getElementById('0');
      panel.childNodes[0].appendChild((component.hostView as any).rootNodes[0]);
      this.addWidgetDialog.hideAddWidgetDialog();
    } else {
      //OnChange won't be triggered by the dashboard, thus we need to update the panels here
      this.dashboardService.updateDashboardPanels(new UpdateDashboardPanelsDTO({
        dashboardId: this.dashboardId,
        addedPanels: [this.mapToDashboarPanelDTO(newPanel)],
      })).subscribe(() => {
        this.addWidgetDialog.hideAddWidgetDialog();
      });
    }
  }

  public refreshWidgetData() {
    if (!this.dashboard) {
      return;
    }

    this.reloadWidgets();
  }

  public addWidget() {
    this.addWidgetDialog.showAddWidgetDialog();
  }

  private onDeleteWidget(panelId: string) {
    if (!panelId) {
      return;
    }
    const dialog = this.modalService.showOkCancelDialog('i18n.domain.home.dashboard.shared.delete-widget-title', 'i18n.domain.home.dashboard.shared.delete-widget-message');
    dialog.onOkSubject.subscribe(() => {
      this.panelDeleted = true;
      this.dashboard.removePanel(panelId);
      //Workaround due to synfusion not triggering onchange if widget is deleted
      this.removeWidget(panelId);
    });
  }

  private mapToDashboarPanelDTO(p: ExtendedPanelModel): DashboardPanelDTO {
    return new DashboardPanelDTO({
      id: +p.id,
      sizeX: p.sizeX,
      sizeY: p.sizeY,
      row: p.row,
      col: p.col,
      widgetType: p.widgetType.toString(),
      widgetParameters: p.widgetParameters
    });
  }

  private reloadWidgets(): void {
    this.dashboard.removeAll();

    while (this.viewContainer.length > 0) {
      this.viewContainer.remove();
    };

    //Sort panels from top left to bottom right
    //Neccessary to insert the panels without issues
    var panels = this.extendedDashboardPanels?.sort((a, b) => a.row - b.row || a.col - b.col);
    panels?.forEach((panel) => {
      //Remove unauthorized widgets
      if (!this.dashboardService.canUserAccesWidget(panel.widgetType as WidgetType)) {
        this.removeWidget(panel.id);
        this.onChange({isInteracted: true, addedPanels: [], changedPanels: [], removedPanels: [panel]});
        return;
      }

      const widgetClass = this.dashboardService.getWidgetClass(panel.widgetType as WidgetType);

      //Remove unknown widgets
      if (!widgetClass) {
       this.removeWidget(panel.id);
        this.onChange({isInteracted: true, addedPanels: [], changedPanels: [], removedPanels: [panel]});
        return;
      }

      //Create syncfusion dashboard node
      this.dashboard.addPanel({
        id: panel.id,
        sizeX: panel.sizeX,
        sizeY: panel.sizeY,
        row: panel.row,
        col: panel.col,
        widgetType: panel.widgetType,
        widgetParameters: panel.widgetParameters
      } as ExtendedPanelModel);
      const panelNode = document.getElementById(panel.id);
      if (!panelNode) {
        return;
      }

      //Create widget component and insert into syncfusion dashboard node
      const component = this.viewContainer.createComponent(widgetClass);
      const instance = component.instance as IAbstractWidgetComponent;
      instance.isPersonalMode = this.dashboardService.isPersonalMode(panel.widgetType as WidgetType);
      instance.initializeParameters(panel.id, panel.widgetParameters, this.onDeleteWidget.bind(this));
      instance.mode = this.isEditMode ? WidgetMode.Edit : WidgetMode.View;
      if (this.isEditMode) {
        this.subscriptions.add(instance.parametersChanged.subscribe((data: ParameterChangedData) => this.handleWidgetParameterChanged(data)));
      }
      this.viewContainer.insert(component.hostView);
      panelNode.childNodes[0].appendChild((component.hostView as any).rootNodes[0]);
    });

    //Workaround ==> Syncfusion is stupid...
    //this.dashboard.removeAll(); removes the panels AND the gridlines
    if (this.isEditMode) {
      this.dashboard.showGridLines = false;
      this.dashboard.showGridLines = true;
    }
  }

  private handleWidgetParameterChanged(data: ParameterChangedData) {
    const panel = this.extendedDashboardPanels.find((p) => p.id === data.panelId);
    if (!panel) {
      return;
    }
    panel.widgetParameters = data.serializedParamerters;
    this.onChange({isInteracted: true, addedPanels: [], changedPanels: [panel], removedPanels: []});
  }

  private removeWidget(panelId: string) {
    this.dashboardService.updateDashboardPanels(new UpdateDashboardPanelsDTO({
      dashboardId: this.dashboardId,
      removedPanelIds: [+panelId]
    })).subscribe();
  }
}
